import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { WORKFORCE_TABS } from '@/Pages/tabRoutes';
import EmployeesTab from './EmployeesTab';
import TimesheetsTab from './TimesheetsTab';
import ExceptionsTab from './ExceptionsTab';
import PolicyTab from './PolicyTab';
import RosterTab from './RosterTab';
import TemplatesTab from './TemplatesTab';
import AvailabilityTab from './AvailabilityTab';
import SwapsTab from './SwapsTab';
import LabourTab from './LabourTab';
import MyScheduleTab from './MyScheduleTab';

/**
 * §4 / ADR-0008 — employee identity, attendance and scheduling. The time
 * clock itself lives at `/time-clock` (its own station route, gated on
 * `attendance.punch` — see `nav.ts`): this screen is the management side,
 * for the people who hold `employees.*`, `attendance.*` or `scheduling.*` —
 * ***and*** the self-service side, for `scheduling.view_own`, which every
 * floor role holds and neither of the other two implies.
 *
 * Compensation is deliberately not a tab here — it is not a management
 * *area*, it is a permission (`compensation.view`/`.manage`) narrower than
 * any of the others, and it is reached from inside a specific employee's
 * own profile, never from a list of everyone's pay at once.
 *
 * Scheduling — `roster`, `templates`, `availability`, `swaps`, `labour` and
 * `mine` — added here rather than as a rival screen, per the gap analysis:
 * rosters, shift templates, availability and time-off, swaps and the labour
 * report were the single largest reach gap in the product (24 routes, 3 file
 * mentions). See `schedulingLib.ts` for the pinned contract and the two
 * gaps it documents (no dry-run conflict check; a manager can add but not
 * remove another employee's availability window).
 *
 * `mine` (`MyScheduleTab`) was the third gap on top of those two: the manager
 * build covered `scheduling.view`/`.manage`/`.publish`/`.swap_approve` in
 * full but never wired the `scheduling.view_own`/`.swap_request` self-service
 * routes — `GET /scheduling/me`, `.../me/availability`, `POST .../me/swaps`,
 * `POST /scheduling/shifts/:id/claim` — which are exactly the four every
 * floor role (WAITER, CASHIER, HEAD_WAITER…) holds and neither `.view` nor
 * `.manage` covers. Listed last in `WORKFORCE_TABS` on purpose: it is the
 * ONLY tab those roles can see, so its position in the array never changes
 * where they land, but a manager who already holds `employees.view` keeps
 * landing on Employees rather than being moved to a tab added after them.
 */
export default function Workforce() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/workforce', WORKFORCE_TABS);
  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('workforce.title')}</h1>
        <p className="text-sm text-ink-muted">{t('workforce.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((entry) => ({ key: entry.id, label: t(entry.labelKey) }))}
        value={current}
        onChange={select}
        label={t('workforce.title')}
        idPrefix="workforce"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'employees' && <EmployeesTab />}
      {current === 'timesheets' && <TimesheetsTab />}
      {current === 'exceptions' && <ExceptionsTab />}
      {current === 'policy' && <PolicyTab />}
      {current === 'roster' && <RosterTab />}
      {current === 'templates' && <TemplatesTab />}
      {current === 'availability' && <AvailabilityTab />}
      {current === 'swaps' && <SwapsTab />}
      {current === 'labour' && <LabourTab />}
      {current === 'mine' && <MyScheduleTab />}
    </div>
  );
}
